import { SearchField } from "@/components/common/SearchField/SearchField"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useVirtualizer } from "@tanstack/react-virtual"
import { Package, Plus, X } from "lucide-react"
import { useMemo, useRef, useState } from "react"
import { SectionLabel } from "../../../shared/SectionLabel"
import { AddArticleDialog, type DraftArticle } from "./AddArticleDialog"
import {
  encodeProductGroup,
  hasDistinctName,
  matchesArticleFilter,
  NO_PRODUCT_GROUP,
} from "./articleFilter"
import { ProductGroupSelect } from "./ProductGroupSelect"

interface ArticlesEditorProps {
  bulk: boolean
  supplierId: string
  articles: DraftArticle[]
  setArticles: (next: DraftArticle[]) => void
}

export function ArticlesEditor({
  bulk,
  supplierId,
  articles,
  setArticles,
}: ArticlesEditorProps) {
  const { translate } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [filter, setFilter] = useState("")
  const [groupChoice, setGroupChoice] = useState("")
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const scrollRef = useRef<HTMLDivElement>(null)

  const needle = filter.trim().toLowerCase()
  // Options come from the draft's own articles: this is a narrowing tool, and
  // org-wide groups the draft does not contain would only be noise.
  const groupOptions = useMemo(() => {
    const counts = new Map<string, number>()
    for (const a of articles) {
      if (a.productGroup) {
        counts.set(a.productGroup, (counts.get(a.productGroup) ?? 0) + 1)
      }
    }
    return [...counts]
      .sort(([x], [y]) => x.localeCompare(y))
      .map(([value, count]) => ({ value, count }))
  }, [articles])
  const hasUngrouped = useMemo(
    () => articles.some((a) => !a.productGroup),
    [articles],
  )
  // A choice whose articles are all gone falls back to "all".
  const choiceExists =
    groupChoice === NO_PRODUCT_GROUP
      ? hasUngrouped
      : groupOptions.some((o) => encodeProductGroup(o.value) === groupChoice)
  const group = choiceExists ? groupChoice : ""
  const filterActive = needle !== "" || group !== ""
  const filtered = useMemo(
    () => articles.filter((a) => matchesArticleFilter(a, needle, group)),
    [articles, needle, group],
  )
  // Only rows that are visible count as selected: an id selected under an
  // earlier filter must not be removed by a click that cannot show it.
  const selectedVisible = useMemo(
    () => filtered.filter((a) => selected.has(a.id)),
    [filtered, selected],
  )

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 36,
    overscan: 8,
  })

  if (bulk) {
    return (
      <div className="flex flex-col gap-2">
        <SectionLabel>{translate("MC_INQ_DRAFT_SEC_ARTICLES")}</SectionLabel>
        <div className="flex items-start gap-2.5 rounded-lg border border-grey4 bg-grey2 px-4 py-3.5">
          <Package className="mt-0.5 h-4 w-4 shrink-0 text-purple1" />
          <p className="text-[11.5px] leading-relaxed text-grey5">
            {translate("MC_INQ_DRAFT_ARTICLES_BULK_NOTE")}
          </p>
        </div>
      </div>
    )
  }

  const allMatchingSelected =
    filtered.length > 0 && selectedVisible.length === filtered.length

  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Selects every match, not just the rows the virtualizer has rendered.
  function toggleAllMatching() {
    setSelected(
      allMatchingSelected ? new Set() : new Set(filtered.map((a) => a.id)),
    )
  }

  // A removal that leaves nothing in view would strand the user behind a stale
  // filter ("0 of N", "no match") with the remaining articles hidden: reset it.
  function resetFilterIfViewEmpty(visibleAfter: number) {
    if (visibleAfter > 0) return
    setFilter("")
    setGroupChoice("")
  }

  function removeSelected() {
    const drop = new Set(selectedVisible.map((a) => a.id))
    setArticles(articles.filter((a) => !drop.has(a.id)))
    setSelected(new Set())
    resetFilterIfViewEmpty(filtered.length - selectedVisible.length)
  }

  // Single-row removal takes the same path: drop the id from the selection
  // (it would otherwise linger and count as selected) and reset a filter that
  // the removal leaves with nothing to show.
  function removeRow(id: string) {
    setArticles(articles.filter((x) => x.id !== id))
    setSelected((prev) => {
      if (!prev.has(id)) return prev
      const next = new Set(prev)
      next.delete(id)
      return next
    })
    resetFilterIfViewEmpty(filtered.length - 1)
  }

  function keepOnlyMatching() {
    setArticles(filtered)
    setSelected(new Set())
    resetFilterIfViewEmpty(filtered.length)
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SectionLabel>{translate("MC_INQ_DRAFT_SEC_ARTICLES")}</SectionLabel>
        <div className="flex items-center gap-3">
          <span className="text-xs text-grey5">
            {!filterActive
              ? articles.length
              : translate("MC_INQ_DRAFT_ART_MATCH_COUNT", {
                  n: filtered.length,
                  total: articles.length,
                })}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDialogOpen(true)}
          >
            <Plus />
            {translate("MC_INQ_DRAFT_ADD_ARTICLE")}
          </Button>
        </div>
      </div>

      {articles.length > 0 && (
        <>
          <div className="flex items-center gap-2">
            <SearchField
              value={filter}
              onChange={setFilter}
              placeholder={translate("MC_INQ_DRAFT_ART_FILTER")}
              onClear={() => setFilter("")}
              className="max-w-none"
            />
            {groupOptions.length > 0 && (
              <ProductGroupSelect
                options={groupOptions}
                value={group}
                onChange={setGroupChoice}
                includeNone={hasUngrouped}
              />
            )}
          </div>
          {/* Left padding = list border + row padding, so this checkbox sits in
              the same vertical line as the row checkboxes below. */}
          <div className="flex flex-wrap items-center gap-3 pl-[calc(0.75rem+1px)]">
            {filtered.length > 0 && (
              <label className="flex cursor-pointer items-center gap-2 text-xs text-grey5">
                <Checkbox
                  checked={allMatchingSelected}
                  onCheckedChange={toggleAllMatching}
                />
                {translate("MC_INQ_DRAFT_ART_SELECT_ALL", {
                  n: filtered.length,
                })}
              </label>
            )}
            <span className="flex-1" />
            {selectedVisible.length > 0 && (
              <Button variant="outline" size="sm" onClick={removeSelected}>
                {translate("MC_INQ_DRAFT_ART_REMOVE_SELECTED", {
                  n: selectedVisible.length,
                })}
              </Button>
            )}
            {filterActive &&
              filtered.length > 0 &&
              filtered.length < articles.length && (
                <Button variant="outline" size="sm" onClick={keepOnlyMatching}>
                  {translate("MC_INQ_DRAFT_ART_KEEP_MATCHING", {
                    n: filtered.length,
                  })}
                </Button>
              )}
          </div>
        </>
      )}

      <div
        ref={scrollRef}
        className="max-h-72 overflow-y-auto rounded-lg border border-grey4 bg-grey2"
      >
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-3 py-8 text-center text-sm text-grey5">
            {translate(
              articles.length === 0
                ? "MC_INQ_DRAFT_ANNEX_EMPTY"
                : "MC_INQ_DRAFT_ART_NO_MATCH",
            )}
            {articles.length === 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDialogOpen(true)}
              >
                <Plus />
                {translate("MC_INQ_DRAFT_ADD_ARTICLE")}
              </Button>
            )}
          </div>
        ) : (
          // Virtualized: a supplier can have five figures of articles.
          <div
            role="list"
            style={{ height: virtualizer.getTotalSize(), position: "relative" }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const a = filtered[virtualRow.index]
              return (
                <div
                  key={a.id}
                  role="listitem"
                  // Windowed: the rows in the DOM are not the whole list.
                  aria-setsize={filtered.length}
                  aria-posinset={virtualRow.index + 1}
                  data-index={virtualRow.index}
                  ref={virtualizer.measureElement}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                  className="flex items-center gap-3 border-b border-grey4/50 px-3 py-2"
                >
                  <Checkbox
                    aria-label={translate("MC_INQ_DRAFT_ART_SELECT_ROW", {
                      addr: a.articleNumber,
                    })}
                    checked={selected.has(a.id)}
                    onCheckedChange={() => toggleRow(a.id)}
                  />
                  <span className="font-mono w-40 shrink-0 truncate text-[12px] text-yellow1">
                    {a.articleNumber}
                  </span>
                  {hasDistinctName(a) ? (
                    <span className="min-w-0 flex-1 truncate text-[12.5px] text-white">
                      {a.name}
                    </span>
                  ) : (
                    <span className="flex-1" />
                  )}
                  <button
                    type="button"
                    aria-label={translate("MC_INQ_DRAFT_REMOVE", {
                      addr: a.articleNumber,
                    })}
                    onClick={() => removeRow(a.id)}
                    className="text-grey5 hover:text-red1"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <AddArticleDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        supplierId={supplierId}
        scope={articles}
        onConfirm={(picked) => setArticles([...articles, ...picked])}
      />
    </div>
  )
}
